/**
 * Adds one or more lines to the cart the way Horizon's product form does, so the cart drawer
 * re-renders in place, auto-opens, and the cart bubble updates.
 *
 * 1. Dispatches CartLinesUpdateEvent with a deferred promise (the drawer and bubble listen).
 * 2. POSTs /cart/add.js with `items[]`, asking for the cart-items sections in the same request.
 * 3. Resolves the promise with the new cart and the rendered sections.
 *
 * Used by the drawer's "Finish the build" quick adds, the buy module and the setup builder.
 *
 * @example
 * import { addLines } from '@theme/dialed-cart';
 * await addLines(element, [{ id: 123, quantity: 1, properties: { Truck: '2024 Toyota Tacoma · 5 ft bed' } }]);
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * @typedef {{ id: number | string, quantity?: number, properties?: Record<string, string> }} CartLine
 */

/** Section ids of every cart-items-component on the page (the drawer, the cart page). */
function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
    .filter(Boolean)
    .join(',');
}

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  return response.json();
}

/**
 * @param {Element} source - Element the event is dispatched from (bubbles to document)
 * @param {CartLine[]} lines
 * @param {{ context?: string, sourceName?: string }} [options]
 * @returns {Promise<{ ok: boolean, message?: string }>}
 */
export async function addLines(source, lines, { context = 'product', sourceName = 'dialed-cart' } = {}) {
  const items = lines.map(({ id, quantity = 1, properties }) => ({ id: Number(id), quantity, properties }));
  const deferred = CartLinesUpdateEvent.createPromise();

  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context,
      lines: items.map(({ id, quantity }) => ({
        merchandiseId: `gid://shopify/ProductVariant/${id}`,
        quantity,
      })),
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchConfig('json', {
        body: JSON.stringify({ items, sections: cartSectionIds(), sections_url: window.location.pathname }),
      }),
    }).then((r) => r.json());

    const cart = await fetchCart();

    if (response.status) {
      source.dispatchEvent(
        new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' })
      );
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, source: sourceName },
      });
      return { ok: false, message: response.description || response.message };
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { didError: false, items: cart.items, sections: response.sections, source: sourceName },
    });
    return { ok: true };
  } catch (error) {
    deferred.reject(error);
    source.dispatchEvent(new CartErrorEvent({ error: 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
    return { ok: false };
  }
}

/**
 * Sets one cart line's quantity (0 removes it) and re-renders the cart sections, the way
 * Horizon's cart items component does.
 *
 * @param {Element} source
 * @param {string} key - The line item key
 * @param {number} quantity
 */
export async function setLineQuantity(source, key, quantity) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: quantity > 0 ? 'update' : 'remove',
      context: 'cart',
      lines: [{ id: key, quantity }],
      promise: deferred.promise,
    })
  );
  try {
    const response = await fetch(Theme.routes.cart_change_url, {
      ...fetchConfig('json', {
        body: JSON.stringify({ id: key, quantity, sections: cartSectionIds(), sections_url: window.location.pathname }),
      }),
    }).then((r) => r.json());
    const cart = await fetchCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { didError: Boolean(response.status), items: cart.items, sections: response.sections, source: 'dialed-cart' },
    });
    return cart;
  } catch (error) {
    deferred.reject(error);
    return null;
  }
}

export { fetchCart };

/**
 * <dialed-quick-add data-variant-id="123"> wraps a button and adds one of that variant on click.
 * Used by "Finish the build" in the cart drawer.
 */
class DialedQuickAdd extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!button || button.disabled || !this.dataset.variantId) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    const { ok } = await addLines(this, [{ id: this.dataset.variantId }], { sourceName: 'dialed-quick-add' });
    // Inside the drawer the re-render replaces this element. Elsewhere (product cards) it stays,
    // so re-enable it, flashing data-added-label on success.
    if (!button.isConnected) return;
    button.removeAttribute('aria-busy');
    if (ok && this.dataset.addedLabel) {
      const label = button.textContent;
      button.textContent = this.dataset.addedLabel;
      setTimeout(() => {
        button.textContent = label;
        button.disabled = false;
      }, 1400);
    } else {
      button.disabled = false;
    }
  };
}

if (!customElements.get('dialed-quick-add')) customElements.define('dialed-quick-add', DialedQuickAdd);
